import { Layout } from "@shopify/polaris";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ICryptoAddress, ICryptoAddressWithTokensAndWallets } from "@novel/shared/interfaces/CryptoAddress";
import { ISODateString } from "@novel/shared/interfaces/shared/ISODateString";
import { ITokenWithMetadataAndCollection } from "@novel/shared/interfaces/Token";
import { ITokenCollectionClaimWithTokenCollection } from "@novel/shared/interfaces/TokenCollectionClaim";
import { IPFS_GATEWAY_PREFIX } from "@novel/shared/utils/appConstants";
import { checkIsUnrevealed } from "@novel/shared/utils/checkIsUnrevealed";
import { GalleryItemCmp } from "@customer-gallery/components/GalleryPageCmp/GalleryList/GalleryItem";
import { ClaimedModal } from "@customer-gallery/components/GalleryPageCmp/ClaimedModal";
import { IBenefits } from "@novel/shared/interfaces/Discounts";
import { createGlobalStyle } from "styled-components";
import find from "lodash/find";
import differenceBy from "lodash/differenceBy";

// unsure why, but for SSR not working to have these styles in global JSX
const GlobalStyle = createGlobalStyle`
    .gallery-list-nft-images-wrapper {
        height: auto;
        width: 100%;
        padding: 0.5rem;
    }

    .nft-header-setting-bar {
        display: flex;
        flex-direction: row;
        justify-content: flex-start;
        flex-wrap: nowrap;
        align-items: center;
        margin-bottom: 20px;
    }

    .bold-selection-text-area {
        font-size: 1.25rem;
        font-weight: 600;
        line-height: 1.2;
        color: #696c74;
    }

    .hide-selection-area-section {
        flex-grow: 1;
        flex-basis: 0px;
        align-items: center;
        justify-content: space-between;
        display: flex;
        flex-direction: row;
        flex-wrap: nowrap;
    }

    .hide-selection-area-section-left {
        flex-grow: 1;
        flex-basis: 0px;
        align-items: center;
        justify-content: flex-end;
        display: flex;
        flex-direction: row;
        flex-wrap: nowrap;
    }

    .hide-selection-area-section-center {
        flex-grow: 1;
        flex-basis: 0px;
        align-items: center;
        justify-content: center;
        display: flex;
        flex-direction: row;
        flex-wrap: nowrap;
    }

    .light-selected-text-area {
        color: #696c74;
        font-size: 0.875rem;
        font-weight: 400;
        line-height: 1.4285714285714286;
    }

    .gallery-list-nft-images-container {
        display: grid !important;
        column-gap: 3.4rem;
        row-gap: 2.4rem;
        grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
    }

    .gallery-list-nft-image-name {
        max-width: 100%;
        text-overflow: ellipsis;
        overflow: hidden;
    }
    @media (min-width: 1024px) {
        .gallery-list-nft-images-wrapper {
            height: calc(100vh - 125px);
            padding: 3.4rem;
        }

        .gallery-list-nft-images-container {
            grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
        }
    }
`;
interface GalleryListProps {
  readonly filterToContract?: string;
  readonly search?: string | null;
  readonly excludeHidden?: boolean;
  readonly filterToAddress?: string;
  readonly discounts?: IBenefits[];
  readonly addresses: ICryptoAddressWithTokensAndWallets[];
  readonly unclaimedTokens?: ITokenCollectionClaimWithTokenCollection[];
  readonly onClickUnclaimed?: (unclaimedLineItem: ITokenCollectionClaimWithTokenCollection) => void;
  readonly skeleton?: boolean;
  readonly disableClaimButtonWhileClaiming?: boolean;
  readonly settingState?: boolean;
  readonly isClaimingToken?: string | null;
}
export function GalleryListCmp(props: GalleryListProps): JSX.Element {
  const skeletonData = useMemo(() => {
    const emptyArray = (Array(12).fill({}) as ITokenWithMetadataAndCollection[]);
    return emptyArray;
  }, []);

  // const [selectedNfts, setSelectedNfts] = useState<ITokenWithMetadataAndCollection[]>([]);
  // const [hidingLoad, setHidingLoad] = useState(false);
  // const dispatch = useNovelCustomerDispatch();

  // const styleProps = useSpring({
  //     from: { top: "-100%" },
  //     top: props.settingState ? "0" : "-100%",
  // });

  const claimedTokens = useMemo(() => {
    let data = props.addresses.filter(cryptoAddress => !props.filterToAddress || cryptoAddress.address === props.filterToAddress).map(cryptoAddress => ({
      ...cryptoAddress,
      cryptoAddressToTokens: (cryptoAddress?.cryptoAddressToTokens || [])?.filter(cryptoAddressToToken => !props.filterToContract || cryptoAddressToToken.token.collectionSmartContract.address === props.filterToContract)
    })).flatMap(cryptoAddress => (cryptoAddress.cryptoAddressToTokens || []).map(cryptoAddressToToken => ({
      cryptoAddress,
      cryptoAddressToToken,
      token: cryptoAddressToToken.token,
      collectionMetadata: cryptoAddressToToken.token.collectionSmartContract?.collectionMetadata,
      tokenMetadata: cryptoAddressToToken.token.tokenMetadata
    }))).sort(({
      cryptoAddressToToken: {
        token: a
      }
    }, {
      cryptoAddressToToken: {
        token: b
      }
    }) => sortByDate(a.createdDate, b.createdDate));
    if (props.search) {
      data = data.filter(itm => itm.collectionMetadata.title?.toLowerCase().indexOf(props.search!.toLowerCase()) !== -1);
    }
    if (props.excludeHidden) {
      data = data.filter(itm => !itm.cryptoAddressToToken.isHidden);
    }
    if (!props.settingState) {
      data = data.filter(itm => !itm.cryptoAddressToToken.isHidden);
    }
    return data;
  }, [props.addresses, props.filterToAddress, props.filterToContract, props.search, props.excludeHidden, props.settingState]);
  const unclaimedTokens = useMemo(() => {
    if (props.unclaimedTokens && !props.filterToContract) {
      const data = props.unclaimedTokens.slice().sort((a, b) => sortByDate(a.updatedDate, b.updatedDate));
      if (props.search) {
        return data.filter(itm => itm.collectionToMintFrom?.collectionMetadata.title?.toLowerCase().indexOf(props.search!.toLowerCase()) !== -1);
      }
      return data;
    }
  }, [props.filterToContract, props.search, props.unclaimedTokens]);
  const [currentClaimedToken, setClaimedToken] = useState<null | ITokenWithMetadataAndCollection>();
  const closeClaimedModal = useCallback(() => {
    setClaimedToken(null);
  }, []);
  const setPrevClaimedToken = useCallback(() => {
    const currentIndex = currentClaimedToken && claimedTokens.findIndex(claimedToken => currentClaimedToken.id === claimedToken.token.id);
    if (typeof currentIndex === "number" && currentIndex >= 0) {
      const prevClaimedToken = claimedTokens[currentIndex - 1];
      if (prevClaimedToken) {
        setClaimedToken(prevClaimedToken.token);
      }
    }
  }, [claimedTokens, currentClaimedToken]);
  const setNextClaimedToken = useCallback(() => {
    const currentIndex = currentClaimedToken && claimedTokens.findIndex(claimedToken => currentClaimedToken.id === claimedToken.token.id);
    if (typeof currentIndex === "number" && currentIndex >= 0) {
      const nextClaimedToken = claimedTokens[currentIndex + 1];
      if (nextClaimedToken) {
        setClaimedToken(nextClaimedToken.token);
      }
    }
  }, [claimedTokens, currentClaimedToken]);
  const [previouslyUnclaimedToken, setPreviouslyUnclaimedToken] = useState<null | ITokenCollectionClaimWithTokenCollection>();
  const [previouslyClaimedTokens, setPreviouslyClaimedTokens] = useState<any[]>();
  useEffect(() => {
    const id = props.isClaimingToken;
    if (id) {
      const token = find(unclaimedTokens, {
        id
      });
      setPreviouslyUnclaimedToken(token);
      setPreviouslyClaimedTokens(claimedTokens);
    } else if (previouslyUnclaimedToken && previouslyClaimedTokens && previouslyClaimedTokens.length) {
      const claimedToken = differenceBy(claimedTokens, previouslyClaimedTokens, token => token.token.id)[0];
      if (claimedToken) {
        setClaimedToken(claimedToken.token);
        setPreviouslyUnclaimedToken(null);
        setPreviouslyClaimedTokens(claimedTokens);
      }
    }
  }, [claimedTokens, unclaimedTokens, props.isClaimingToken, previouslyClaimedTokens, previouslyUnclaimedToken]);

  ////Please don't remove this disabling multi hide for now.
  // const selectNFTs = (token: ITokenWithMetadataAndCollection) => {
  //     setSelectedNfts((prev) => {
  //         const check_item = prev.find(
  //             (item) =>
  //                 item.tokenId === token.tokenId &&
  //                 item.collectionSmartContract.address === token.collectionSmartContract.address,
  //         );
  //         if (check_item) {
  //             const index = prev.findIndex(
  //                 (object) =>
  //                     object.tokenId === token.tokenId &&
  //                     object.collectionSmartContract.address ===
  //                     token.collectionSmartContract.address,
  //             );
  //             if (index > -1) {
  //                 prev.splice(index, 1);
  //             }
  //             return [...prev];
  //         } else {
  //             return [...prev, { ...token }];
  //         }
  //     });
  // };

  ////Please don't remove this disabling multi hide for now.
  // const toggleNftsHidden = useAsyncCallback(async () => {
  //     setHidingLoad(true);
  //     let hideStatus: boolean;
  //     for (let index = 0; index < selectedNfts.length; index++) {
  //         const element = selectedNfts[index];
  //         hideStatus = selectedNfts.filter((item) => item.isHidden === false).length > 0;
  //         await typedFrontendGalleryApi.postReq("/api/customer/hide-nft", {
  //             reqBody: {
  //                 contractAddress: element.collectionSmartContract.address,
  //                 tokenId: element.tokenId,
  //                 isHidden: hideStatus,
  //             },
  //         });

  //         dispatch({
  //             type: "TOGGLE_TOKEN_HIDDEN",
  //             payload: {
  //                 id: element.id,
  //                 status: hideStatus,
  //             },
  //         });
  //     }
  //     setSelectedNfts((prev) => {
  //         return prev.map((item) => {
  //             return { ...item, isHidden: hideStatus };
  //         });
  //     });
  //     setHidingLoad(false);
  // }, [selectedNfts, dispatch]);

  return <React.Fragment>
            <GlobalStyle />
            <Layout>
                <Layout.Section fullWidth>
                    <div className="gallery-list-nft-images-wrapper">
                        {/* {props.settingState && (
                            <animated.div style={styleProps}>
                                <div className="nft-header-setting-bar">
                                    <div className="hide-selection-area-section">
                                        <span className="light-selected-text-area">
                                            {selectedNfts.length} items selected
                                        </span>
                                        {selectedNfts.length > 0 && (
                                            <Button loading={hidingLoad} onClick={toggleNftsHidden}>
                                                {selectedNfts.filter(
                                                    (item) => item.isHidden === false,
                                                ).length > 0
                                                    ? "Hide"
                                                    : "Show"}
                                            </Button>
                                        )}
                                    </div>
                                    <div className="hide-selection-area-section-center">
                                        <span className="bold-selection-text-area">
                                            Select NFTs to customize
                                        </span>
                                    </div>
                                    <div className="hide-selection-area-section-left">
                                        <span className="light-selected-text-area">Show only hidden</span>
                                    </div>
                                </div>
                            </animated.div>
                         )} */}
                        <div className="gallery-list-nft-images-container">
                            {props.skeleton && skeletonData.map((lineItem, index) => <GalleryItemCmp
            // selectNFTs={selectNFTs}
            key={typeof lineItem.id !== "undefined" && lineItem.id || index} settingState={props.settingState} image="" claimedToken={lineItem} cryptoAddress={({} as ICryptoAddress)} skeleton={props.skeleton} />)}
                            {unclaimedTokens && unclaimedTokens.map(lineItem => <GalleryItemCmp key={lineItem.id} hasConnectedAddress={!!props.addresses.length} name={lineItem.collectionToMintFrom.collectionMetadata.title!} quantity={lineItem.quantity} // re-architect to allow for more than one
            image={lineItem.collectionToMintFrom.collectionMetadata.cloudImage || (lineItem.collectionToMintFrom.collectionMetadata.ipfsImageCid ? `${IPFS_GATEWAY_PREFIX}${lineItem.collectionToMintFrom.collectionMetadata.ipfsImageCid}` : "")} unclaimedToken={lineItem} onClick={props.onClickUnclaimed} disableClaimButtonWhileClaiming={props.disableClaimButtonWhileClaiming} />)}
                            {claimedTokens.map(({
              cryptoAddress,
              cryptoAddressToToken,
              token,
              collectionMetadata,
              tokenMetadata
            }) => <GalleryItemCmp
            // selectNFTs={selectNFTs}
            key={cryptoAddressToToken.id} cryptoAddressToToken={cryptoAddressToToken} settingState={props.settingState} hasConnectedAddress={!!props.addresses.length} isUnrevealed={checkIsUnrevealed(collectionMetadata)} name={checkIsUnrevealed(collectionMetadata) ? `${collectionMetadata.title} Unrevealed #${token.tokenId}` : tokenMetadata?.title || `${collectionMetadata.title} #${token.tokenId}`}
            // re-architect to allow for more than one
            quantity={cryptoAddressToToken.tokenQuantity} image={tokenMetadata?.cloudImage ? tokenMetadata.cloudImage : tokenMetadata?.ipfsImageCid ? tokenMetadata?.ipfsImageCid ? tokenMetadata.ipfsImageCid.startsWith("https") ? `${tokenMetadata.ipfsImageCid}` : `${IPFS_GATEWAY_PREFIX}${tokenMetadata.ipfsImageCid}` : "" : collectionMetadata?.cloudImage ? collectionMetadata.cloudImage : collectionMetadata.ipfsImageCid ? `${IPFS_GATEWAY_PREFIX}${collectionMetadata.ipfsImageCid}` : ""} video={tokenMetadata?.cloudVideo || tokenMetadata?.ipfsVideoCid ? `${IPFS_GATEWAY_PREFIX}${tokenMetadata.ipfsImageCid}` : null || tokenMetadata?.animationUrl} cryptoAddress={cryptoAddress} claimedToken={token} onClick={(setClaimedToken as any)} />)}
                        </div>
                    </div>
                </Layout.Section>
            </Layout>
            {currentClaimedToken && <ClaimedModal onClose={closeClaimedModal} claimedToken={currentClaimedToken} setPrevClaimedToken={setPrevClaimedToken} setNextClaimedToken={setNextClaimedToken} discounts={props.discounts} />}
        </React.Fragment>;
}
function sortByDate(a: ISODateString, b: ISODateString) {
  return +ISODateString.fromString(a).toJSDate() > +ISODateString.fromString(b).toJSDate() ? -1 : 1;
}